import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    useDisclosure,
    useColorModeValue,
    Portal
} from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import UserSidebar from './UserSidebar';
import Navbar from './Navbar';
import BackToTop from '../Common/BackToTop';
import ToastManagerComponent, { ToastManager } from '../Common/ToastManager';
import apiService from '../../services/apiService';
import { useNavigate } from 'react-router-dom';

/**
 * Support Desk User Layout Component
 */
function UserLayout({ children, userProfile, appConfig, onError }) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [sidebarVisible, setSidebarVisible] = useState(true);
    const location = useLocation();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    
    // Track count of newly toasted alerts for badge display
    const [newToastedAlertsCount, setNewToastedAlertsCount] = useState(() => {
        try {
            const stored = sessionStorage.getItem('userNewToastedAlertsCount');
            return stored ? parseInt(stored, 10) : 0;
        } catch {
            return 0;
        }
    });
    
    // Issues management statistics for navbar/sidebar display
    const [issuesStats, setIssuesStats] = useState({
        totalIssues: 0,
        openIssues: 0,
        takenIssues: 0,
        clientsWithIssues: 0
    });
    const navigate = useNavigate();
    
    // notification tracking for smart toasting - using sessionStorage for persistence
    const [latestAlertToasted, setLatestAlertToasted] = useState(() => {
        try {
            const stored = sessionStorage.getItem('userLatestAlertToasted');
            if (stored) {
                const parsedIds = JSON.parse(stored);
                return new Set(parsedIds);
            }
            return new Set();
        } catch (error) {
            console.warn('Failed to restore userLatestAlertToasted from sessionStorage:', error);
            return new Set();
        }
    });
    
    const [allNotifications, setAllNotifications] = useState([]);
    
    // Use sessionStorage to persist first load state across re-renders
    const [isFirstLoad, setIsFirstLoad] = useState(() => {
        try {
            const stored = sessionStorage.getItem('userNotificationFirstLoad');
            return stored === null ? true : JSON.parse(stored);
        } catch {
            return true;
        }
    });
    
    // Refs to track notification state and prevent duplicate processing
    const notificationQueue = useRef([]);
    const activeToasts = useRef(new Set());
    const maxConcurrentToasts = 3;
    const isProcessingNotifications = useRef(false);
    const isQueueProcessing = useRef(false);
    const notificationInterval = useRef(null);
    
    // Issues polling interval
    const issuesInterval = useRef(null);

    // Chakra UI theme-aware colors
    const mainBg = useColorModeValue('gray.50', 'gray.900');

    /**
     * Handle route changes to reset notification badge count
     */
    useEffect(() => {
        if (location.pathname === '/user-notifications') {
            setNewToastedAlertsCount(0);
            try {
                sessionStorage.setItem('userNewToastedAlertsCount', '0');
            } catch (error) {
                console.warn('Failed to reset userNewToastedAlertsCount in sessionStorage:', error);
            }
        }
        
        // Refresh issues stats when navigating to issues-related pages
        if (location.pathname === '/issues' || location.pathname === '/user-analytics') {
            fetchIssuesStats();
        }
    }, [location.pathname]);

    /**
     * Fetch issues statistics for dashboard/navbar display
     */
    const fetchIssuesStats = useCallback(async () => {
        try {
            const [issueCountsResponse, takenCountResponse] = await Promise.all([
                apiService.getIssueCountByClient(),
                apiService.getTakenIssueCount()
            ]);

            let stats = {
                totalIssues: 0,
                openIssues: 0,
                takenIssues: 0,
                clientsWithIssues: 0
            };

            if (issueCountsResponse.success) {
                const issueCounts = issueCountsResponse.data || {};
                const clientsWithIssues = Object.keys(issueCounts).length;
                const totalOpenIssues = Object.values(issueCounts).reduce((sum, count) => sum + count, 0);
                
                stats.openIssues = totalOpenIssues;
                stats.clientsWithIssues = clientsWithIssues;
            }

            if (takenCountResponse.success) {
                stats.takenIssues = takenCountResponse.data?.takenIssueCount || 0;
            }

            // Calculate total issues (open + taken, since taken issues are also open)
            stats.totalIssues = stats.openIssues;

            setIssuesStats(stats);

        } catch (error) {
            console.error('Failed to fetch issues statistics:', error);
        }
    }, []);

    /**
     * Start polling for issues statistics
     */
    const startIssuesPolling = useCallback(() => {
        // Initial load
        fetchIssuesStats();
        
        // Set up interval for polling every 60 seconds (less frequent than notifications)
        issuesInterval.current = setInterval(() => {
            fetchIssuesStats();
        }, 60000);
    }, [fetchIssuesStats]);

    /**
     * Stop issues polling
     */
    const stopIssuesPolling = useCallback(() => {
        if (issuesInterval.current) {
            clearInterval(issuesInterval.current);
            issuesInterval.current = null;
        }
    }, []);

    /**
     * Initialize layout with configuration
     * Sets up API service and other global services
     */
    const initializeLayout = useCallback(async () => {
        try {
            if (appConfig) {
                // Initialize API service with configuration
                apiService.initialize(appConfig);
            }
        } catch (error) {
            console.error('User layout initialization failed:', error);
            onError?.(error);
        }
    }, [appConfig, onError]);

    /**
     * Stop notification polling
     * Clears the polling interval and resets all processing state
     */
    const stopNotificationPolling = useCallback(() => {
        if (notificationInterval.current) {
            clearInterval(notificationInterval.current);
            notificationInterval.current = null;
        }
        isProcessingNotifications.current = false;
        isQueueProcessing.current = false;
        activeToasts.current.clear();
        notificationQueue.current = [];
    }, []);

    /**
     * Process new notifications with enhanced sliding window approach for user role
     * Implements proper sliding window for latestAlertToasted
     */
    const processNewNotificationsEnhanced = useCallback(async (currentNotifications) => {
        try {
            // Extract current alert IDs
            const currentAlertIds = currentNotifications.map(n => n.id);
            
            // Calculate difference: current alerts - previously toasted alerts
            const newAlertIds = currentAlertIds.filter(alertId => !latestAlertToasted.has(alertId));
            
            if (newAlertIds.length > 0) {
                // Get the actual notification objects for new alerts
                const newAlerts = currentNotifications.filter(notification => 
                    newAlertIds.includes(notification.id)
                );

                // Sort new alerts by creation time (newest first) for proper toasting order
                const sortedNewAlerts = newAlerts.sort((a, b) => 
                    new Date(b.createdAt) - new Date(a.createdAt)
                );

                // Add ALL new alerts to queue
                sortedNewAlerts.forEach(alert => {
                    notificationQueue.current.push(alert);
                });

                // Start processing the queue if not already processing
                if (!isQueueProcessing.current) {
                    processNotificationQueue();
                }

                // Increment badge count for newly toasted alerts
                const newBadgeCount = newToastedAlertsCount + sortedNewAlerts.length;
                setNewToastedAlertsCount(newBadgeCount);
                
                // Persist badge count to sessionStorage
                try {
                    sessionStorage.setItem('userNewToastedAlertsCount', newBadgeCount.toString());
                } catch (error) {
                    console.warn('Failed to save userNewToastedAlertsCount to sessionStorage:', error);
                }

                // Sliding window approach for latestAlertToasted
                const updatedLatestAlertToasted = new Set();
                
                // Keep only alerts that are still in current results
                for (const alertId of latestAlertToasted) {
                    if (currentAlertIds.includes(alertId)) {
                        updatedLatestAlertToasted.add(alertId);
                    }
                }
                
                // Add only the NEW alerts that were just toasted
                newAlertIds.forEach(alertId => {
                    updatedLatestAlertToasted.add(alertId);
                });
                
                // Update state with the sliding window result
                setLatestAlertToasted(updatedLatestAlertToasted);
                
                // Persist to sessionStorage
                try {
                    sessionStorage.setItem('userLatestAlertToasted', JSON.stringify(Array.from(updatedLatestAlertToasted)));
                } catch (error) {
                    console.warn('Failed to save updated userLatestAlertToasted to sessionStorage:', error);
                }
            } else {
                // Even when no new alerts, still clean up latestAlertToasted
                const cleanedLatestAlertToasted = new Set();
                
                for (const alertId of latestAlertToasted) {
                    if (currentAlertIds.includes(alertId)) {
                        cleanedLatestAlertToasted.add(alertId);
                    }
                }
                
                // Only update if cleanup actually removed something
                if (cleanedLatestAlertToasted.size !== latestAlertToasted.size) {
                    setLatestAlertToasted(cleanedLatestAlertToasted);
                    
                    // Persist to sessionStorage
                    try {
                        sessionStorage.setItem('userLatestAlertToasted', JSON.stringify(Array.from(cleanedLatestAlertToasted)));
                    } catch (error) {
                        console.warn('Failed to save cleaned userLatestAlertToasted to sessionStorage:', error);
                    }
                }
            }

        } catch (error) {
            console.error('Error processing new notifications for user:', error);
        }
    }, [latestAlertToasted, newToastedAlertsCount]);

    /**
     * Process notification queue with smart concurrent toast management
     */
    const processNotificationQueue = useCallback(async () => {
        if (isQueueProcessing.current || notificationQueue.current.length === 0) {
            return;
        }

        isQueueProcessing.current = true;

        while (notificationQueue.current.length > 0) {
            // Wait if we already have maximum concurrent toasts
            while (activeToasts.current.size >= maxConcurrentToasts) {
                await new Promise(resolve => setTimeout(resolve, 500));
            }

            // Get next notification from queue
            const notification = notificationQueue.current.shift();
            if (notification) {
                // Show toast immediately
                showSingleNotificationToastQueued(notification);
                
                // Small delay between showing toasts for better UX
                if (notificationQueue.current.length > 0) {
                    await new Promise(resolve => setTimeout(resolve, 300));
                }
            }
        }

        isQueueProcessing.current = false;
    }, []);

    /**
     * Show a single notification toast with queue management for user role
     */
    const showSingleNotificationToastQueued = useCallback((notification) => {
        try {
            const getNotificationType = (status) => {
                switch (status?.toLowerCase()) {
                    case 'error':
                        return 'error';
                    case 'failed':
                        return 'error';
                    case 'success':
                        return 'success';
                    case 'completed':
                        return 'success';
                    case 'warning':
                        return 'warning';
                    case 'critical':
                        return 'error';
                    case 'pending':
                        return 'warning';
                    default:
                        return 'info';
                }
            };

            // Enhanced message formatting with category and timestamp
            const category = notification.category || 'Alert';
            const message = notification.message || 'New notification received';
            const formattedMessage = `${category}: ${message}`;
            
            const type = getNotificationType(notification.status);
            const toastId = `user_alert_${notification.id}_${Date.now()}`;
            const duration = 5000;

            // Add to active toasts tracking
            activeToasts.current.add(toastId);

            // Use direct toast methods for better reliability
            let toastResult;
            switch (type) {
                case 'error':
                    toastResult = ToastManager.showError(formattedMessage, {
                        duration: duration,
                        position: 'bottom-right',
                        id: toastId
                    });
                    break;
                case 'success':
                    toastResult = ToastManager.showSuccess(formattedMessage, {
                        duration: duration,
                        position: 'bottom-right',
                        id: toastId
                    });
                    break;
                case 'warning':
                    toastResult = ToastManager.showWarning(formattedMessage, {
                        duration: duration,
                        position: 'bottom-right',
                        id: toastId
                    });
                    break;
                default:
                    toastResult = ToastManager.showInfo(formattedMessage, {
                        duration: duration,
                        position: 'bottom-right',
                        id: toastId
                    });
                    break;
            }

            // Auto-remove from tracking after duration + small buffer
            setTimeout(() => {
                if (activeToasts.current.has(toastId)) {
                    activeToasts.current.delete(toastId);
                }
            }, duration + 500);

        } catch (error) {
            console.error('Error showing queued notification toast for user:', error);
            // Fallback: Remove from active tracking to prevent blocking
            if (activeToasts.current.size > 0) {
                const toastIds = Array.from(activeToasts.current);
                const oldestToast = toastIds[0];
                activeToasts.current.delete(oldestToast);
            }
        }
    }, []);

    /**
     * Enhanced notification fetching for user role
     * Fetches notifications that the user has access to based on their client permissions
     */
    const fetchNotificationsEnhanced = useCallback(async () => {
        // Prevent concurrent processing
        if (isProcessingNotifications.current) {
            return;
        }

        try {
            isProcessingNotifications.current = true;
            
            // Fetch latest 50 notifications from API (filtered by user's client access)
            const response = await apiService.getLatestAlerts(50);
            
            if (response.success && response.data) {
                // Handle both array response and paginated response structure
                const fetchedNotifications = Array.isArray(response.data) 
                    ? response.data 
                    : response.data.content || [];
                
                // Map API fields to component fields for all 50 notifications
                const processedNotifications = fetchedNotifications.map(notification => ({
                    ...notification,
                    id: notification.alertId || notification.id,
                    createdAt: notification.createdAtUtc || notification.createdAt,
                    isRead: notification.isRead || false
                }));

                // Store all 50 notifications for complete tracking
                setAllNotifications(processedNotifications);
                
                // Get latest 3 for display in navbar dropdown
                const latestThreeForDisplay = processedNotifications.slice(0, 3);
                setNotifications(latestThreeForDisplay);
                
                // Calculate unread count from all notifications
                const unreadCountAll = processedNotifications.filter(n => !n.isRead).length;
                setUnreadCount(unreadCountAll);

                // Enhanced notification processing based on first load vs subsequent loads
                if (isFirstLoad) {
                    // First load: Initialize latestAlertToasted without showing toasts
                    const currentAlertIds = new Set(processedNotifications.map(n => n.id));
                    
                    // Initialize tracking with current alerts (no toasts on initial load)
                    setLatestAlertToasted(currentAlertIds);
                    
                    // Persist to sessionStorage
                    try {
                        sessionStorage.setItem('userLatestAlertToasted', JSON.stringify(Array.from(currentAlertIds)));
                    } catch (error) {
                        console.warn('Failed to save userLatestAlertToasted to sessionStorage:', error);
                    }
                    
                    // Mark first load as complete and persist to sessionStorage
                    setIsFirstLoad(false);
                    try {
                        sessionStorage.setItem('userNotificationFirstLoad', 'false');
                    } catch (error) {
                        console.warn('Failed to save user notification first load state:', error);
                    }
                } else {
                    // Subsequent loads: Calculate difference and toast new alerts
                    await processNewNotificationsEnhanced(processedNotifications);
                }
            } else {
                console.warn('API response unsuccessful or no data for user notifications:', {
                    success: response.success,
                    hasData: !!response.data,
                    response
                });
            }
        } catch (error) {
            console.error('Failed to fetch notifications for user:', error);
            // Don't show error for notification failures to avoid spam
        } finally {
            isProcessingNotifications.current = false;
        }
    }, [isFirstLoad, latestAlertToasted, processNewNotificationsEnhanced]);

    /**
     * Start polling for notifications with enhanced queuing logic for user role
     */
    const startNotificationPolling = useCallback(() => {
        // Initial load - get latest 50 and initialize tracking
        fetchNotificationsEnhanced();
        
        // Set up interval for polling every 30 seconds with queue check
        const interval = 30000;
        notificationInterval.current = setInterval(() => {
            // Only fetch next batch if queue is empty and not currently processing
            if (notificationQueue.current.length === 0 && 
                activeToasts.current.size === 0 && 
                !isQueueProcessing.current) {
                fetchNotificationsEnhanced();
            }
        }, interval);
    }, [fetchNotificationsEnhanced]);

    /**
     * Initialize layout and start background services including issues polling
     */
    useEffect(() => {
        initializeLayout();
        startNotificationPolling();
        startIssuesPolling(); // Start issues statistics polling
        
        // Cleanup on unmount
        return () => {
            stopNotificationPolling();
            stopIssuesPolling(); // Cleanup issues polling
        };
    }, [initializeLayout, startNotificationPolling, stopNotificationPolling, startIssuesPolling, stopIssuesPolling]);

    /**
     * Handle notification click with enhanced tracking
     */
    const handleNotificationClick = async (notification) => {
        try {
            // Mark as read if not already read
            if (!notification.isRead) {
                // Update local state for both display notifications and all notifications
                setNotifications(prev => 
                    prev.map(n => 
                        n.id === notification.id 
                            ? { ...n, isRead: true } 
                            : n
                    )
                );
                
                setAllNotifications(prev =>
                    prev.map(n =>
                        n.id === notification.id
                            ? { ...n, isRead: true }
                            : n
                    )
                );
                
                // Update unread count
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
            
            // Navigate to user notifications page for full view
            // window.location.href = window.ADI_CONFIG?.app?.subDirectory + '/user-notifications';
           navigate('/user-notifications');
            
        } catch (error) {
            console.error('Failed to handle notification click for user:', error);
        }
    };

    /**
     * Handle sidebar open for mobile
     */
    const handleSidebarOpen = () => {
        if (window.innerWidth >= 1024) {
            setSidebarVisible(!sidebarVisible);
        } else {
            onOpen();
        }
    };

    /**
     * Handle sidebar close for mobile
     */
    const handleSidebarClose = () => {
        onClose();
    };

    /**
     * Enhanced cleanup on user sign out including issues stats
     */
    useEffect(() => {
        // Listen for auth state changes to reset notification tracking on sign out
        const handleAuthStateChange = () => {
            if (!userProfile) {
                setLatestAlertToasted(new Set());
                setAllNotifications([]);
                setNotifications([]);
                setUnreadCount(0);
                setNewToastedAlertsCount(0);
                setIsFirstLoad(true);
                setIssuesStats({ // Reset issues stats
                    totalIssues: 0,
                    openIssues: 0,
                    takenIssues: 0,
                    clientsWithIssues: 0
                });
                notificationQueue.current = [];
                activeToasts.current.clear();
                isQueueProcessing.current = false;
                stopNotificationPolling();
                stopIssuesPolling(); // Stop issues polling
                
                // Reset sessionStorage for next login
                try {
                    sessionStorage.removeItem('userNotificationFirstLoad');
                    sessionStorage.removeItem('userNewToastedAlertsCount');
                    sessionStorage.removeItem('userLatestAlertToasted');
                } catch (error) {
                    console.warn('Failed to clear user notification state:', error);
                }
            }
        };

        // Reset tracking when userProfile becomes null (sign out)
        if (!userProfile && !isFirstLoad) {
            handleAuthStateChange();
        }
    }, [userProfile, isFirstLoad, stopNotificationPolling, stopIssuesPolling]);

    return (
        <Box minH="100vh" maxH="100vh" bg={mainBg} className="user-layout-main" overflow="hidden">
            {/* Desktop Sidebar - Fixed positioning */}
            <Box
                display={{ base: 'none', lg: sidebarVisible ? 'block' : 'none' }}
                position="fixed"
                left={0}
                top={0}
                h="100vh"
                w="250px"
                zIndex={1001}
            >
                <UserSidebar
                    variant="sidebar"
                    userProfile={userProfile}
                    appConfig={appConfig}
                    onClose={handleSidebarClose}
                    issuesStats={issuesStats}
                />
            </Box>

            {/* Mobile Sidebar Drawer */}
            <UserSidebar
                isOpen={isOpen}
                onClose={handleSidebarClose}
                variant="drawer"
                userProfile={userProfile}
                appConfig={appConfig}
                issuesStats={issuesStats} // Pass issues statistics to mobile drawer
            />
            
            {/* Main Content Area - Proper margin for desktop sidebar */}
            <Box
                ml={{ base: 0, lg: sidebarVisible ? '250px' : 0 }}
                transition="margin-left 0.3s ease"
                h="100vh"
                position="relative"
                display="flex"
                flexDirection="column"
                className="user-layout-content"
            >
                {/* Navigation Bar - Enhanced with notification props for user role */}
                <Navbar
                    onOpen={handleSidebarOpen}
                    userProfile={userProfile}
                    notifications={notifications}
                    unreadCount={unreadCount}
                    newToastedAlertsCount={newToastedAlertsCount}
                    issuesStats={issuesStats}
                    sidebarVisible={sidebarVisible}
                    onNotificationClick={handleNotificationClick}
                    onNotificationsView={() => navigate('/notifications')}
                    appConfig={appConfig}
                />
                
                {/* Page Content Container - Make this the scrollable area */}
                <Box
                    as="main"
                    p={{ base: 4, md: 6 }}
                    pt={{ base: 6, md: 8 }}
                    flex="1"
                    bg={mainBg}
                    position="relative"
                    data-scroll-container="true"
                    overflowY="auto"
                    h="calc(100vh - 64px)"
                >
                    {children}
                </Box>
            </Box>
            
            {/* Back to Top Button - Portal for proper z-index */}
            <Portal>
                <Box className="back-to-top-button">
                    <BackToTop />
                </Box>
            </Portal>
            
            {/* Toast Manager for Enhanced Notifications */}
            <ToastManagerComponent />
        </Box>
    );
}

export default UserLayout;